/**
 * PulsePeopleCard — per-person impact leaderboard for the Pulse Home Dashboard.
 *
 * Renders a `DashboardCardShell` containing:
 *  - A top-5 leaderboard. Each row is `name` + a two-segment bar whose LENGTH
 *    encodes that person's TOTAL activity relative to the busiest person (top =
 *    full width), so relative impact reads at a glance. The bar is SPLIT into
 *    scans (Gripp orange) vs messages (light orange) — the only two honest
 *    per-person signals we have.
 *  - A bottom legend (Scans · Messages).
 *  - An Activity-chart-style hover tooltip over each bar showing the raw counts
 *    and share, so the split reads unambiguously (web-only; portalled to <body>).
 *
 * The old Active / Scans / Replies header was removed — "Active" was not real
 * data (see GRIPP #2923 / the gripp-os PeopleCard reference this ports).
 *
 * Clicking a person filters the field map to that person (a `person` facet in
 * the dashboard filter): `onSelectPerson` toggles it, `selectedPersonId` marks
 * the active row. "View all" navigates to /people.
 *
 * Consumes `useHomePeople(range)` — the root `homePeopleCard` query. Loading /
 * empty / error states delegate to the `DashboardCardShell` slots. All text
 * cells use styled <Text> — never bare strings (Times New Roman trap).
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { useHomePeople, type HomePersonRow } from '../hooks/useHomePeople';
import { TimeRange } from '../types';

// Scans vs messages split — two shades of Gripp orange: scans use the brand
// orange, messages a much lighter shade from the same palette for clear
// contrast. Matches the gripp-os PeopleCard reference.
const SCANS_COLOR = tokens.colors.brand.core; // Gripp brand orange (#FF5505)
const MSGS_COLOR = tokens.colors.orange[300]; // lighter tier of the same palette (#FFB368)

export interface PulsePeopleCardProps {
  range: TimeRange;
  /**
   * Account id of the person the field map is currently filtered to, or null.
   * Marks the matching row active. People is NOT map-plottable (no plotMode);
   * this is a separate `person` facet that scopes the resting ASSET view.
   */
  selectedPersonId?: string | null;
  /**
   * Toggle the person filter. The dashboard scopes the field map to this
   * person's scanned assets; re-selecting the active person clears it.
   */
  onSelectPerson?: (person: { id: string; name: string }) => void;
}

// Hovered bar state — the person + the bar's viewport rect (anchors the
// portalled tooltip). Web-only; null when nothing is hovered.
interface HoverState {
  person: HomePersonRow;
  rect: DOMRect;
}

// ─── LegendDot ──────────────────────────────────────────────────────────────
const LegendDot: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <View style={styles.legendItem}>
    <View style={[styles.legendDot, { backgroundColor: color }]} />
    <Text style={styles.legendLabel}>{label}</Text>
  </View>
);

// ─── PersonRow ────────────────────────────────────────────────────────────────
// One leaderboard row: name (flex-1, truncated) + an impact bar (flex-1). The
// coloured bar fills `widthPct` of the bar track (total activity vs the busiest
// person) and is split scans|messages. Pressing toggles the person filter.
interface PersonRowProps {
  row: HomePersonRow;
  /** Bar length as a percentage of the track (total / busiest total). */
  widthPct: number;
  active: boolean;
  onPress: () => void;
  onHoverIn: (rect: DOMRect) => void;
  onHoverOut: () => void;
}

const PersonRow: React.FC<PersonRowProps> = ({
  row,
  widthPct,
  active,
  onPress,
  onHoverIn,
  onHoverOut,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      style={[
        styles.row,
        (active || hovered) && styles.rowActive,
      ]}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={row.name}
      accessibilityState={{ selected: active }}
      testID={`pulse-people-card-row-${row.id}`}
    >
      <Text style={styles.name} numberOfLines={1}>
        {row.name}
      </Text>
      {/* Track fills the row's right half; the coloured bar scales to total. */}
      <View style={styles.barTrack}>
        <View
          style={[styles.barFill, { width: `${widthPct}%` }]}
          // Hover events only fire on web; safe on native (noop). Reports the
          // bar's viewport rect so the portalled tooltip can anchor above it.
          onPointerEnter={(e: { currentTarget: unknown }) =>
            onHoverIn((e.currentTarget as HTMLElement).getBoundingClientRect())
          }
          onPointerLeave={onHoverOut}
        >
          {/* Segments split that length by scans vs messages. */}
          <View
            style={[
              styles.barSeg,
              styles.barSegFirst,
              { flex: row.scans, backgroundColor: SCANS_COLOR },
            ]}
          />
          <View
            style={[
              styles.barSeg,
              styles.barSegLast,
              { flex: row.replies, backgroundColor: MSGS_COLOR },
            ]}
          />
        </View>
      </View>
    </Pressable>
  );
};

// ─── PersonTooltip ──────────────────────────────────────────────────────────
// Activity-chart-style hover tooltip: the bar shows this person's split of
// scans vs messages; the rows give the raw counts + share so it reads
// unambiguously. Portalled to <body> with fixed viewport coords so it escapes
// the bottom card strip's ScrollView clip and floats over the map (web-only).
const TOOLTIP_HALF_WIDTH = 90;
const VIEWPORT_PADDING = 8;
const TOOLTIP_GAP = 8;

const PersonTooltip: React.FC<{ person: HomePersonRow; rect: DOMRect }> = ({
  person,
  rect,
}) => {
  const { t } = useTranslation();
  const total = person.scans + person.replies || 1;
  const scansPct = Math.round((person.scans / total) * 100);
  const msgsPct = 100 - scansPct;

  // Centre on the bar; clamp within the viewport so it never runs off-screen.
  const anchorTop = rect.top - TOOLTIP_GAP;
  const anchorLeft = rect.left + rect.width / 2;
  const clampedLeft = Math.min(
    Math.max(anchorLeft, TOOLTIP_HALF_WIDTH + VIEWPORT_PADDING),
    window.innerWidth - TOOLTIP_HALF_WIDTH - VIEWPORT_PADDING
  );

  const tooltip = (
    <View
      style={[
        styles.tooltip,
        // position:'fixed' is web-only — RNW forwards it; RN types omit it.
        {
          position: 'fixed' as unknown as 'absolute',
          left: clampedLeft,
          top: anchorTop,
          transform: [
            { translateX: '-50%' as unknown as number },
            { translateY: '-100%' as unknown as number },
          ],
        },
      ]}
      pointerEvents="none"
    >
      <TooltipRow
        color={SCANS_COLOR}
        label={t('home.people.scans', 'Scans')}
        value={person.scans}
        pct={scansPct}
      />
      <TooltipRow
        color={MSGS_COLOR}
        label={t('home.people.messages', 'Messages')}
        value={person.replies}
        pct={msgsPct}
      />
    </View>
  );

  return createPortal(tooltip, document.body);
};

const TooltipRow: React.FC<{
  color: string;
  label: string;
  value: number;
  pct: number;
}> = ({ color, label, value, pct }) => (
  <View style={styles.tooltipRow}>
    <View style={[styles.tooltipDot, { backgroundColor: color }]} />
    <Text style={styles.tooltipLabel}>{label}</Text>
    <Text style={styles.tooltipValue}>{value}</Text>
    <Text style={styles.tooltipPct}>{pct}%</Text>
  </View>
);

// ─── PulsePeopleCard ──────────────────────────────────────────────────────────

export const PulsePeopleCard: React.FC<PulsePeopleCardProps> = ({
  range,
  selectedPersonId,
  onSelectPerson,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomePeople(range);
  const [hover, setHover] = useState<HoverState | null>(null);

  const title = t('home.people.title', 'People');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/people')}
      testID="pulse-people-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-people-card"
        loadingSlot={
          <View style={styles.loadingList}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={18} radius={4} />
            ))}
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  if (error) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-people-card"
        errorSlot={
          <ErrorState
            title={t('home.cards.error', 'Could not load data')}
            testID="pulse-people-card-error"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  if (!data || data.people.length === 0) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-people-card"
        emptySlot={
          <EmptyState
            title={t('home.people.empty', 'No activity for this period')}
            testID="pulse-people-card-empty"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  // Top-5 contributors. Bar length encodes each person's TOTAL activity relative
  // to the busiest (top = full width), so the leaderboard reads as impact.
  const top = data.people.slice(0, 5);
  const maxTotal = Math.max(...top.map((p) => p.scans + p.replies), 1);

  return (
    // allowOverflow: the bar hover tooltip is portalled to <body>, but the card
    // body must not clip the row bg bleed (-8 margin) at its rounded edge.
    <DashboardCardShell title={title} action={action} testID="pulse-people-card">
      <View style={styles.cardBody}>
        <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          testID="pulse-people-card-list"
        >
          {top.map((p) => {
            const total = p.scans + p.replies || 1;
            const widthPct = (total / maxTotal) * 100;
            return (
              <PersonRow
                key={p.id}
                row={p}
                widthPct={widthPct}
                active={selectedPersonId === p.id}
                onPress={() => onSelectPerson?.({ id: p.id, name: p.name })}
                onHoverIn={(rect) => setHover({ person: p, rect })}
                onHoverOut={() => setHover(null)}
              />
            );
          })}
        </ScrollView>

        {/* Legend for the two segments. */}
        <View style={styles.legend} testID="pulse-people-card-legend">
          <LegendDot color={SCANS_COLOR} label={t('home.people.scans', 'Scans')} />
          <LegendDot
            color={MSGS_COLOR}
            label={t('home.people.messages', 'Messages')}
          />
        </View>
      </View>

      {/* Hover tooltip — web-only, portalled so it floats above the map. */}
      {Platform.OS === 'web' && hover && (
        <PersonTooltip person={hover.person} rect={hover.rect} />
      )}
    </DashboardCardShell>
  );
};

PulsePeopleCard.displayName = 'PulsePeopleCard';

const styles = StyleSheet.create({
  cardBody: {
    flex: 1,
    gap: tokens.spacing.sm, // gap-2 (8px)
  },

  // Leaderboard list ────────────────────────────────────────────────────────────
  // The list and its rows use a symmetric -8 margin / +8 padding so the row
  // hover/selection highlight bleeds 8px into the card gutter on BOTH sides
  // (matches the reference -mx-2 px-2; fixes the old asymmetric right-edge gap).
  list: {
    flex: 1,
    minHeight: 0,
    marginHorizontal: -tokens.spacing.sm,
  },
  listContent: {
    paddingHorizontal: tokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm, // gap-2 (8px)
    paddingVertical: tokens.spacing.sm, // py-2 (8px)
    paddingHorizontal: tokens.spacing.sm, // px-2 (8px)
    marginHorizontal: -tokens.spacing.sm, // -mx-2
    borderRadius: tokens.borderRadius.sm,
  },
  rowActive: {
    backgroundColor: tokens.colors.slate[100],
  },
  name: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: tokens.colors.neutral[800],
    flex: 1,
  },
  // Bar track fills the row's right portion; the coloured fill scales within it.
  barTrack: {
    flex: 1,
    flexDirection: 'row',
  },
  barFill: {
    flexDirection: 'row',
    height: 8, // design: h-2
    borderRadius: 4,
    overflow: 'hidden',
    gap: 2,
  },
  barSeg: {
    height: '100%' as const,
  },
  barSegFirst: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  barSegLast: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },

  // Legend ────────────────────────────────────────────────────────────────────
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md, // gap-3 (12px)
    paddingHorizontal: tokens.spacing.xs,
    paddingTop: tokens.spacing.xs,
    flexShrink: 0,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6, // design: gap-1.5 (6px)
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
  },
  legendLabel: {
    ...tokens.typography.label.small,
    fontSize: 11,
    lineHeight: 15,
    color: tokens.colors.slate[400],
  },

  // Loading skeleton ────────────────────────────────────────────────────────────
  loadingList: {
    gap: tokens.spacing.sm,
  },

  // Hover tooltip (web-only, portalled) ───────────────────────────────────────
  tooltip: {
    backgroundColor: tokens.colors.neutral[700],
    borderRadius: tokens.borderRadius.sm,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    minWidth: 120,
    zIndex: 9999,
    gap: tokens.spacing.xxs,
    ...Platform.select({
      web: { boxShadow: '0 6px 20px rgba(0,0,0,0.18)' } as object,
    }),
  },
  tooltipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  tooltipDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  tooltipLabel: {
    ...tokens.typography.label.small,
    color: tokens.colors.neutral[400],
    width: 56,
  },
  tooltipValue: {
    ...tokens.typography.label.small,
    fontWeight: '600',
    color: tokens.colors.brand.white,
    width: 28,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
  tooltipPct: {
    ...tokens.typography.label.small,
    color: tokens.colors.neutral[400],
    width: 36,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
});
